const PREAMBLE: string = `#version 300 es
precision highp float;
precision highp int;
`;

export const HS_CAD_FULLSCREEN_VERTEX_SOURCE: string = `${PREAMBLE}
out vec2 v_uv;
void main() {
  // Oversized triangle covering the viewport; no vertex buffers are needed.
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  v_uv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

// Shared depth helpers. u_proj holds the projection entries (m00, m11, m22, m32); u_ortho is 1 for orthographic.
const DEPTH_HELPERS: string = `
uniform highp sampler2D u_depth;
uniform vec4 u_proj;
uniform float u_ortho;
uniform vec2 u_size;

float linearDepth(float d) {
  float ndc = d * 2.0 - 1.0;
  return u_ortho > 0.5 ? (u_proj.w - ndc) / u_proj.z : u_proj.w / (ndc + u_proj.z);
}

float depthAt(ivec2 c) {
  ivec2 s = ivec2(u_size);
  return texelFetch(u_depth, clamp(c, ivec2(0), s - 1), 0).r;
}

vec3 viewPosition(ivec2 c) {
  float dist = linearDepth(depthAt(c));
  vec2 ndc = (vec2(c) + 0.5) / u_size * 2.0 - 1.0;
  vec2 xy = u_ortho > 0.5 ? ndc / u_proj.xy : ndc * dist / u_proj.xy;
  return vec3(xy, -dist);
}

// Smallest-difference neighbour per axis keeps normals clean across silhouettes.
vec3 viewNormal(ivec2 c, vec3 p) {
  vec3 pl = viewPosition(c + ivec2(-1, 0));
  vec3 pr = viewPosition(c + ivec2(1, 0));
  vec3 pd = viewPosition(c + ivec2(0, -1));
  vec3 pu = viewPosition(c + ivec2(0, 1));
  vec3 dx = abs(pr.z - p.z) < abs(p.z - pl.z) ? pr - p : p - pl;
  vec3 dy = abs(pu.z - p.z) < abs(p.z - pd.z) ? pu - p : p - pd;
  vec3 n = normalize(cross(dx, dy));
  vec3 toCamera = u_ortho > 0.5 ? vec3(0.0, 0.0, 1.0) : normalize(-p);
  return dot(n, toCamera) < 0.0 ? -n : n;
}

float interleavedNoise(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}
`;

export const HS_CAD_AO_FRAGMENT_SOURCE: string = `${PREAMBLE}
${DEPTH_HELPERS}
uniform float u_scale;
uniform float u_radiusPx;
uniform int u_samples;
in vec2 v_uv;
out vec4 o_ao;

void main() {
  ivec2 c = ivec2((gl_FragCoord.xy) * u_scale);
  float d = depthAt(c);
  if (d >= 1.0) {
    o_ao = vec4(1.0);
    return;
  }
  vec3 p = viewPosition(c);
  vec3 n = viewNormal(c, p);
  float dist = -p.z;
  // Convert the pixel radius into a view-space radius at this depth.
  float worldRadius = u_radiusPx * 2.0 / u_size.y * (u_ortho > 0.5 ? 1.0 : dist) / u_proj.y;
  float noise = interleavedNoise(gl_FragCoord.xy);
  float occlusion = 0.0;
  for (int i = 0; i < 32; i++) {
    if (i >= u_samples) break;
    float t = (float(i) + noise) / float(u_samples);
    float angle = (float(i) + noise) * 2.39996323;
    float r = max(1.5, t * u_radiusPx);
    ivec2 sc = c + ivec2(round(vec2(cos(angle), sin(angle)) * r));
    vec3 v = viewPosition(sc) - p;
    float len = length(v);
    float facing = max(dot(n, v) / max(len, 1e-5) - 0.12, 0.0);
    float range = clamp(1.0 - len / worldRadius, 0.0, 1.0);
    occlusion += facing * range;
  }
  o_ao = vec4(vec3(1.0 - clamp(occlusion / float(u_samples) * 3.5, 0.0, 1.0)), 1.0);
}
`;

export const HS_CAD_COMPOSITE_FRAGMENT_SOURCE: string = `${PREAMBLE}
${DEPTH_HELPERS}
uniform sampler2D u_color;
uniform sampler2D u_ao;
uniform float u_aoScale;
uniform float u_aoIntensity;
uniform int u_aoBlurRadius;
uniform float u_edgeIntensity;
uniform float u_pixelAngle;
uniform float u_tonemap;
uniform float u_tonemapStrength;
uniform float u_saturation;
uniform float u_warmth;
uniform float u_keyLight;
uniform float u_sharpen;
uniform int u_edgeWidth;
uniform float u_gradient;
uniform float u_vignette;
uniform vec3 u_clearColor;
in vec2 v_uv;
out vec4 o_color;

vec3 filmic(vec3 x) {
  // Narkowicz ACES fit, blended back toward the input so BIM palette colors stay recognisable.
  vec3 a = (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14);
  return mix(x, clamp(a, 0.0, 1.0), u_tonemapStrength);
}

float invDepth(ivec2 c) {
  float d = depthAt(c);
  return d >= 1.0 ? 0.0 : 1.0 / linearDepth(d);
}

// Point cloud draws leave alpha at 0.5 (see the render pass); their sparse depth is not a surface.
bool isPointPixel(ivec2 c) {
  return texelFetch(u_color, clamp(c, ivec2(0), ivec2(u_size) - 1), 0).a < 0.75;
}

float surfaceInvDepth(ivec2 c, float centerInv) {
  return isPointPixel(c) ? centerInv : invDepth(c);
}

float edgeAmount(ivec2 c, float centerInv) {
  if (centerInv == 0.0 || isPointPixel(c)) return 0.0;
  int w = u_edgeWidth;
  float l = surfaceInvDepth(c + ivec2(-w, 0), centerInv);
  float r = surfaceInvDepth(c + ivec2(w, 0), centerInv);
  float b = surfaceInvDepth(c + ivec2(0, -w), centerInv);
  float t = surfaceInvDepth(c + ivec2(0, w), centerInv);
  // 1/z is linear across a plane, so its second difference vanishes on flat surfaces and
  // spikes on silhouettes and creases.
  float lap = max(abs(l + r - 2.0 * centerInv), abs(b + t - 2.0 * centerInv)) / centerInv;
  // A crease's second difference grows with the sampling step, so the threshold follows the edge width.
  float threshold = u_pixelAngle * 0.5 * float(w);
  return smoothstep(threshold, threshold * 4.0, lap);
}

void main() {
  ivec2 c = ivec2(gl_FragCoord.xy);
  vec4 color = texelFetch(u_color, c, 0);
  float d = depthAt(c);
  if (d >= 1.0) {
    color.a = 1.0;
    if (u_gradient > 0.5) {
      float g = gl_FragCoord.y / u_size.y;
      color.rgb = mix(u_clearColor * 0.88, mix(u_clearColor, vec3(1.0), 0.6), g);
      if (u_vignette > 0.5) {
        // Warm-grey studio backdrop: bright center, cooler darker corners, as in Autodesk Viewer.
        vec2 q = gl_FragCoord.xy / u_size - 0.5;
        float r = clamp(length(q * vec2(1.0, 1.15)) * 1.35, 0.0, 1.0);
        vec3 center = vec3(0.945, 0.943, 0.935);
        vec3 corner = vec3(0.872, 0.885, 0.89);
        color.rgb = mix(center, corner, r * r);
      }
    }
    o_color = color;
    return;
  }
  bool pointPixel = color.a < 0.75;
  color.a = 1.0;
  if (u_sharpen > 0.0 && !pointPixel) {
    ivec2 last = ivec2(u_size) - 1;
    vec3 around = texelFetch(u_color, clamp(c + ivec2(-1, 0), ivec2(0), last), 0).rgb
      + texelFetch(u_color, clamp(c + ivec2(1, 0), ivec2(0), last), 0).rgb
      + texelFetch(u_color, clamp(c + ivec2(0, -1), ivec2(0), last), 0).rgb
      + texelFetch(u_color, clamp(c + ivec2(0, 1), ivec2(0), last), 0).rgb;
    color.rgb = clamp(color.rgb + (color.rgb - around * 0.25) * u_sharpen, 0.0, 1.0);
  }
  float ao = 1.0;
  if (u_aoIntensity > 0.0 && !pointPixel) {
    float centerDist = linearDepth(d);
    float sum = 0.0;
    float weights = 0.0;
    for (int y = -u_aoBlurRadius; y < u_aoBlurRadius; y++) {
      for (int x = -u_aoBlurRadius; x < u_aoBlurRadius; x++) {
        ivec2 sc = c + ivec2(x, y);
        float sd = depthAt(sc);
        float w = sd >= 1.0 ? 0.0 : 1.0 / (1.0 + abs(linearDepth(sd) - centerDist) / centerDist * 80.0);
        sum += texelFetch(u_ao, ivec2(vec2(sc) / u_aoScale), 0).r * w;
        weights += w;
      }
    }
    ao = weights > 0.0 ? sum / weights : 1.0;
    ao = u_aoIntensity <= 1.0 ? mix(1.0, ao, u_aoIntensity) : pow(ao, u_aoIntensity);
  }
  vec3 rgb = color.rgb * ao;
  if (u_keyLight > 0.0 && !pointPixel) {
    vec3 n = viewNormal(c, viewPosition(c));
    float key = max(dot(n, normalize(vec3(-0.45, 0.6, 0.65))), 0.0);
    // Shade only: surfaces facing the key keep their color, so the light never washes out near geometry.
    rgb *= mix(1.0, 0.68 + 0.32 * key, u_keyLight);
  }
  if (u_tonemap > 0.5) {
    rgb = filmic(rgb);
  }
  float luma = dot(rgb, vec3(0.2126, 0.7152, 0.0722));
  rgb = mix(vec3(luma), rgb, u_saturation);
  rgb *= vec3(1.0 + 0.08 * u_warmth, 1.0 + 0.015 * u_warmth, 1.0 - 0.08 * u_warmth);
  if (u_edgeIntensity > 0.0) {
    float e = edgeAmount(c, invDepth(c));
    rgb = mix(rgb, vec3(0.10, 0.11, 0.14), e * u_edgeIntensity);
  }
  o_color = vec4(clamp(rgb, 0.0, 1.0), color.a);
}
`;
